import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { SelectField } from "../components/ui/select-field.js";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { FileInput } from "../components/ui/file-input.js";

import { HR_ROLES } from "@hrms/types";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string; employeeId: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), employeeId: String(p["employeeId"] ?? "") };
  } catch { return { role: "", employeeId: "" }; }
}

interface StatusHistoryEntry { status: string | null; changedAt: string }
interface GrievanceNote {
  id: string; body: string; createdAt: string;
  author: { id: string; firstName: string; lastName: string };
}
interface GrievanceAttachment {
  id: string; filename: string; mimeType: string | null; createdAt: string;
  uploadedBy: { id: string; firstName: string; lastName: string };
}

interface GrievanceCase {
  id: string; subject: string; description: string; category: string;
  isPosh: boolean; status: string; confidential: boolean;
  resolution: string | null; resolvedAt: string | null; createdAt: string;
  raisedBy: { id: string; firstName: string; lastName: string; employeeCode: string };
  assignedTo: { id: string; firstName: string; lastName: string } | null;
  statusHistory?: StatusHistoryEntry[];
  notes?: GrievanceNote[];
  attachments?: GrievanceAttachment[];
}

const statusBadge = (s: string): React.CSSProperties => {
  if (s === "RESOLVED" || s === "CLOSED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "UNDER_REVIEW") return { background: "var(--color-info-bg)", color: "var(--color-info-fg)" };
  if (s === "WITHDRAWN") return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
  return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};

export default function GrievancePage() {
  usePageBack("/dashboard");
  const grievanceCategories = useLookup("GRIEVANCE_CATEGORY");
  const accessToken = getAccessToken() ?? "";
  const { role, employeeId } = decodeToken(accessToken);
  const isHR = HR_ROLES.has(role);

  const [cases, setCases] = useState<GrievanceCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<GrievanceCase | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category: "GENERAL", isPosh: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPosh, setFilterPosh] = useState<"" | "true" | "false">("");
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachUrls, setAttachUrls] = useState<Record<string, string>>({});

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterPosh) params.set("isPosh", filterPosh);
      const res = await api.get<{ data: GrievanceCase[] }>(`/grievances?${params}`);
      setCases(res.data.data);
    } catch (err) { logError("GrievancePage.load", err); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [filterStatus, filterPosh]);

  async function openCase(id: string) {
    const res = await api.get<GrievanceCase>(`/grievances/${id}`);
    setSelected(res.data);
    for (const att of res.data.attachments ?? []) loadAttachUrl(id, att.id);
  }

  async function loadAttachUrl(caseId: string, attId: string) {
    if (attachUrls[attId]) return;
    try {
      const { data } = await api.get<{ url: string }>(`/grievances/${caseId}/attachments/${attId}/url`);
      setAttachUrls(prev => ({ ...prev, [attId]: data.url }));
    } catch (err) { logError("GrievancePage.loadAttachUrl", err); }
  }

  async function addNote() {
    if (!selected || !noteText.trim()) throw new Error("validation");
    setAddingNote(true);
    try {
      await api.post(`/grievances/${selected.id}/notes`, { note: noteText.trim() });
      setNoteText("");
      openCase(selected.id);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to add note");
      throw e;
    } finally { setAddingNote(false); }
  }

  async function uploadAttachment(file: File) {
    if (!selected) return;
    setUploading(true);
    try {
      const r = await api.post<{ attachment: { id: string }; uploadUrl: string }>(
        `/grievances/${selected.id}/attachments`,
        { filename: file.name, mimeType: file.type }
      );
      const uploadRes = await fetch(r.data.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      if (!uploadRes.ok) throw new Error("S3 upload failed");
      await api.patch(`/grievances/${selected.id}/attachments/${r.data.attachment.id}/confirm-upload`);
      openCase(selected.id);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to upload attachment");
    } finally { setUploading(false); }
  }

  async function create() {
    setSaving(true); setError("");
    try {
      await api.post("/grievances", form);
      setShowForm(false); setForm({ subject: "", description: "", category: grievanceCategories[0]?.value ?? "GENERAL", isPosh: false });
      load();
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function updateStatus(id: string, status: string) {
    try {
      await api.patch(`/grievances/${id}`, { status });
      openCase(id); load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
    }
  }

  async function resolve(id: string) {
    const resolution = prompt("Enter resolution details:");
    if (resolution === null) return;
    try {
      await api.patch(`/grievances/${id}`, { status: "RESOLVED", resolution });
      openCase(id); load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
    }
  }

  async function withdraw(id: string) {
    try {
      await api.patch(`/grievances/${id}/withdraw`);
      openCase(id); load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
      throw e;
    }
  }

  return (
    <div style={{ padding: "var(--space-8)", maxWidth: 1200 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text-900)" }}>Grievance & POSH</h1>
          <p style={{ fontSize: 13, color: "var(--color-text-500)", marginTop: 2 }}>All cases are confidential by default.</p>
        </div>
        <button onClick={() => { setShowForm(true); setError(""); }}
          style={{ padding: "8px 16px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
          Raise Grievance
        </button>
      </div>

      {showForm && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Raise a Grievance</h3>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Subject *</label>
              <input style={inputStyle} value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief summary of the issue" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Category</label>
              <SelectField value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {grievanceCategories.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </SelectField>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Description *</label>
              <textarea rows={5} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Provide full details of the incident, including dates, people involved, and what happened…" />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={form.isPosh} onChange={e => setForm(f => ({ ...f, isPosh: e.target.checked, category: e.target.checked ? "POSH" : f.category }))} />
              <strong>This is a POSH (Prevention of Sexual Harassment) complaint</strong>
            </label>
            {form.isPosh && (
              <div style={{ marginTop: 8, padding: 12, background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: "var(--radius-md)", fontSize: 13, color: "var(--color-warning-fg)" }}>
                POSH complaints are handled with highest confidentiality and will be escalated to the POSH committee.
              </div>
            )}
          </div>
          {error && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <AsyncButton onClick={() => create()} disabled={saving || !form.subject || !form.description}>
              Submit Grievance
            </AsyncButton>
            <button onClick={() => setShowForm(false)} style={{ padding: "7px 16px", background: "transparent", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["", "OPEN", "UNDER_REVIEW", "RESOLVED", "CLOSED", "WITHDRAWN"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
            background: filterStatus === s ? "var(--color-brand-600)" : "var(--color-surface-100)",
            color: filterStatus === s ? "#fff" : "var(--color-text-600)",
            border: filterStatus === s ? "none" : "1px solid var(--color-surface-200)",
          }}>{s || "All"}</button>
        ))}
        {isHR && (
          <button onClick={() => setFilterPosh(p => p === "true" ? "" : "true")} style={{
            padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
            background: filterPosh === "true" ? "var(--color-error)" : "var(--color-surface-100)",
            color: filterPosh === "true" ? "#fff" : "var(--color-text-600)",
            border: filterPosh === "true" ? "none" : "1px solid var(--color-surface-200)",
          }}>POSH Only</button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "380px 1fr" : "1fr", gap: 20 }}>
        <div>
          {loading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p> : cases.length === 0 ? (
            <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>No grievance cases found.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {cases.map(c => (
                <div key={c.id} onClick={() => openCase(c.id)} style={{
                  padding: 14, background: "var(--color-surface-0)",
                  border: selected?.id === c.id ? "2px solid var(--color-brand-500)" : "1px solid var(--color-surface-200)",
                  borderRadius: "var(--radius-lg)", cursor: "pointer",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                        {c.subject}
                        {c.isPosh && <span style={{ fontSize: 10, padding: "1px 6px", background: "var(--color-error)", color: "#fff", borderRadius: 999, fontWeight: 700 }}>POSH</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--color-text-500)", marginTop: 2 }}>
                        {isHR ? `${c.raisedBy.firstName} ${c.raisedBy.lastName} · ` : ""}{c.category}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0, ...statusBadge(c.status) }}>{c.status.replace("_", " ")}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--color-text-400)", marginTop: 6 }}>{new Date(c.createdAt).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
                  {selected.subject}
                  {selected.isPosh && <span style={{ fontSize: 11, marginLeft: 8, padding: "2px 8px", background: "var(--color-error)", color: "#fff", borderRadius: 999, fontWeight: 700 }}>POSH</span>}
                </h3>
                <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 999, fontWeight: 600, ...statusBadge(selected.status) }}>{selected.status.replace("_", " ")}</span>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-400)", display: "inline-flex", alignItems: "center" }}><X size={16} /></button>
            </div>

            <div style={{ fontSize: 13, color: "var(--color-text-600)", marginBottom: 12 }}>
              <strong>Raised by:</strong> {selected.raisedBy.firstName} {selected.raisedBy.lastName} ({selected.raisedBy.employeeCode})<br />
              <strong>Category:</strong> {selected.category}
              {selected.assignedTo && <> · <strong>Assigned to:</strong> {selected.assignedTo.firstName} {selected.assignedTo.lastName}</>}
            </div>

            <div style={{ background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 16, fontSize: 14, color: "var(--color-text-700)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {selected.description}
            </div>

            {selected.resolution && (
              <div style={{ background: "var(--color-success-bg-subtle)", border: "1px solid var(--color-success-border)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 16, fontSize: 13 }}>
                <strong>Resolution:</strong> {selected.resolution}
                {selected.resolvedAt && <div style={{ fontSize: 11, color: "var(--color-text-400)", marginTop: 4 }}>Resolved {new Date(selected.resolvedAt).toLocaleDateString()}</div>}
              </div>
            )}

            {selected.statusHistory && selected.statusHistory.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-500)", textTransform: "uppercase", marginBottom: 8 }}>Status Timeline</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {selected.statusHistory.map((h, i) => (
                    <div key={i} style={{ fontSize: 12, color: "var(--color-text-600)", display: "flex", gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>{(h.status ?? "").replace("_", " ")}</span>
                      <span style={{ color: "var(--color-text-400)" }}>{new Date(h.changedAt).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isHR && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-500)", textTransform: "uppercase", marginBottom: 8 }}>Investigation Notes</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                  {(selected.notes ?? []).map(n => (
                    <div key={n.id} style={{ background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", padding: "10px 12px", fontSize: 13 }}>
                      <div style={{ whiteSpace: "pre-wrap", color: "var(--color-text-700)" }}>{n.body}</div>
                      <div style={{ fontSize: 11, color: "var(--color-text-400)", marginTop: 4 }}>
                        {n.author.firstName} {n.author.lastName} · {new Date(n.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {(selected.notes ?? []).length === 0 && <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>No notes yet.</p>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                    value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add an investigation note…" />
                  <AsyncButton onClick={() => addNote()} disabled={addingNote || !noteText.trim()} className="shrink-0">
                    Add Note
                  </AsyncButton>
                </div>
              </div>
            )}

            {isHR && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-500)", textTransform: "uppercase", marginBottom: 8 }}>Documents</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                  {(selected.attachments ?? []).map(a => (
                    <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, padding: "6px 10px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)" }}>
                      {attachUrls[a.id]
                        ? <a href={attachUrls[a.id]} target="_blank" rel="noreferrer" style={{ color: "var(--color-brand-600)", textDecoration: "none" }}>{a.filename}</a>
                        : <span>{a.filename}</span>}
                      <span style={{ fontSize: 11, color: "var(--color-text-400)" }}>{a.uploadedBy.firstName} {a.uploadedBy.lastName}</span>
                    </div>
                  ))}
                  {(selected.attachments ?? []).length === 0 && <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>No documents yet.</p>}
                </div>
                <FileInput disabled={uploading} placeholder="Attach document" value={null}
                  onChange={f => { if (f) uploadAttachment(f); }} />
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {isHR && selected.status === "OPEN" && (
                <button onClick={() => updateStatus(selected.id, "UNDER_REVIEW")}
                  style={{ padding: "6px 14px", background: "var(--color-info-bg)", color: "var(--color-info-fg)", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  Mark Under Review
                </button>
              )}
              {isHR && ["OPEN", "UNDER_REVIEW"].includes(selected.status) && (
                <button onClick={() => resolve(selected.id)}
                  style={{ padding: "6px 14px", background: "var(--color-success)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  Resolve
                </button>
              )}
              {isHR && selected.status === "RESOLVED" && (
                <button onClick={() => updateStatus(selected.id, "CLOSED")}
                  style={{ padding: "6px 14px", background: "var(--color-surface-200)", color: "var(--color-text-700)", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  Close Case
                </button>
              )}
              {selected.raisedBy.id === employeeId && ["OPEN", "UNDER_REVIEW"].includes(selected.status) && (
                <AsyncButton
                  variant="destructive"
                  onClick={() => (window.confirm("Withdraw this grievance?") ? withdraw(selected.id) : undefined)}
                >
                  Withdraw
                </AsyncButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
